<template>
    <common-page flex-column>
        <div class="card d-flex flex-row align-items-center mb-1 p-1">
            <b-form-select class="w-200px" v-model="drawType"
                           :options="drawTypeList"></b-form-select>
            <b-button class="ml-auto" variant="danger" @click="deleteFeature">Delete</b-button>
            <b-button class="ml-1" @click="undo">Undo</b-button>
            <b-button class="ml-1" variant="warning" @click="cloneFeatures">Clone</b-button>
            <b-button class="ml-1" variant="outline-info" @click="mapView">View</b-button>
            <b-overlay :show="saveLoading" spinner-variant="primary" spinner-type="grow" spinner-small>
                <b-button class="ml-1" variant="outline-success" @click="saveData">
                    <i class="fa fa-save" aria-hidden="true"></i>
                    Save
                </b-button>
            </b-overlay>
        </div>
        <b-overlay :show="saveLoading || !initFinish" spinner-variant="primary" class="w-100 h-100">
            <div v-show="tag" id="map" class="w-100 h-100"></div>
            <customized-diagram-view v-if="!tag" ref="mapView" :img-url="imgUrl"
                                     class="w-100 h-100"></customized-diagram-view>
        </b-overlay>
    </common-page>
</template>

<script>
import {Feature, Map, View} from "ol";
import {Image as ImageLayer, Vector as VectorLayer} from "ol/layer";
import {ImageStatic, Vector as VectorSource} from "ol/source";
import {Draw, Modify, Select, Translate} from 'ol/interaction'
import {createBox, createRegularPolygon} from 'ol/interaction/Draw';
import {Polygon} from "ol/geom";
import * as olProj from "ol/proj";
import 'ol/ol.css';
import CommonPage from "@/components/echarts-part/stuff/CommonPage";
import CustomizedDiagramView from "@/components/LayoutMaps/openLayers/CustomizedDiagramView";
import {_getExtent} from "@/components/LayoutMaps/openLayers/utils/MapUtils";

export default {
    name: "CustomizedDiagramEdit",
    components: {CustomizedDiagramView, CommonPage},
    data() {
        return {
            mapCode: 'Area',
            imgUrl: "",//example: http://localhost:6812/em-admin/emAttachment/download?attachmentId=295832
            mapJson: undefined,

            map: null,
            drawType: '',
            drawTypeList: [
                {value: 'Square', text: '正方形'},
                {value: 'Box', text: '矩形'},
                {value: 'Polygon', text: '多边形'},
                {value: 'None', text: '无'},
            ],
            draw: undefined,
            vectorLayer: undefined,
            drawSource: undefined,

            select: undefined,
            translate: undefined,
            modify: undefined,

            idIndex: 1,
            tag: true,

            initFinish: false,
            saveLoading: false,
        }
    },
    computed: {
        features() {
            if (this.drawSource) {
                return this.drawSource.getFeatures();
            }
            return [];
        },
        selectedFeatures() {
            if (this.select) {
                return this.select.getFeatures();
            }
            return [];
        }
    },
    watch: {
        drawType() {
            this.map.removeInteraction(this.draw)
            this.addInteraction();
        },
        imgUrl(val, oldVal) {
            if (val !== oldVal && this.imgUrl) {
                this.initMap();
            }
        },
        initFinish() {
            if (this.initFinish) {
                //监听键盘事件
                document.addEventListener('keydown', this.whenKeyDown);
            }
        }
    },
    mounted() {
        let params = this.$route.params;
        if (params.mapCode) this.mapCode = params.mapCode;
        if (params.mapJson) this.mapJson = params.mapJson;
        if (params.imgUrl) this.imgUrl = params.imgUrl;
    },
    beforeDestroy() {
        document.removeEventListener('keydown', this.whenKeyDown);
    },
    methods: {
        //初始化openLayers组件
        initMap() {
            this.initFinish = false;

            this.map = new Map({
                target: 'map',
                view: new View({
                    center: olProj.fromLonLat([0, 0]),
                    zoom: 7
                })
            });

            // 计算图片映射到地图上的范围,保持比例的情况下,让图片中心点和地图中心点重合
            _getExtent(this.imgUrl).then(extent => {
                let imageStatic = new ImageStatic({
                    url: this.imgUrl,
                    imageExtent: olProj.transformExtent(extent.extent, 'EPSG:4326', 'EPSG:3857'), //映射到地图的范围
                });
                // 加载图片图层
                this.map.addLayer(new ImageLayer({
                    source: imageStatic
                }));
                this.drawSource = new VectorSource({wrapX: false});
                this.vectorLayer = new VectorLayer({
                    source: this.drawSource,
                });
                this.map.addLayer(this.vectorLayer);

                if (this.mapJson) {
                    //初始化区域数据
                    this.editGeoJson(this.mapJson);
                }

                this.initSelection();

                this.drawType = 'Box';
                this.initFinish = true;
            }).catch(() => {
                this.$message.error('图片加载失败');
            });
        },
        //初始化｜重制-区域选择｜编辑
        initSelection() {
            //重制
            this.map.removeInteraction(this.select);
            this.map.removeInteraction(this.translate);
            this.map.removeInteraction(this.modify);
            //初始化
            this.select = new Select();
            this.translate = new Translate({
                features: this.select.getFeatures()
            });
            this.modify = new Modify({
                features: this.select.getFeatures()
            });
            this.map.addInteraction(this.select);
            this.map.addInteraction(this.translate);
            this.map.addInteraction(this.modify);
        },
        //初始化draw画笔
        addInteraction() {
            let value = this.drawType;
            if (value !== 'None') {
                this.select.setActive(false);
                let geometryFunction;
                if (value === 'Square') {
                    value = 'Circle';
                    geometryFunction = createRegularPolygon(4);
                } else if (value === 'Box') {
                    value = 'Circle';
                    geometryFunction = createBox();
                }
                this.draw = new Draw({
                    source: this.drawSource,
                    type: value,
                    geometryFunction: geometryFunction,
                });
                this.draw.on('drawend', e => {
                    e.feature.setId(this.featureIdGenerator());
                });
                this.map.addInteraction(this.draw);
            } else {
                this.select.setActive(true);
            }
        },
        undo() {
            this.draw.removeLastPoint();
        },
        //复制所选区域
        cloneFeatures() {
            let tempList = [];
            this.selectedFeatures.forEach(f => {
                let temp = f.clone();
                temp.setId(this.featureIdGenerator(tempList.length + 1));
                temp.setStyle(null);
                tempList.push(temp);
            });
            this.initSelection();
            this.drawSource.addFeatures(tempList);
            tempList.forEach(o => {
                this.selectedFeatures.push(o);
            });
        },
        //删除所选区域
        deleteFeature() {
            this.selectedFeatures.forEach(f => {
                this.drawSource.removeFeature(f);
            });
        },
        //输出所有区域坐标数据.json
        getAllFeatureCoordinates() {
            let featureList = [];
            this.features.forEach(f => {
                let coordinateList = this.transformCoordinates(f.getGeometry().getCoordinates(), false);
                //组建geo json格式
                featureList.push({
                    type: "Feature",
                    properties: {
                        name: f.getId()
                    },
                    geometry: {
                        type: "Polygon",
                        coordinates: coordinateList
                    }
                })
            })
            let result = {
                type: 'FeatureCollection',
                mapName: this.mapCode,
                features: featureList
            };
            return JSON.stringify(result);
        },
        transformCoordinates(list, fromLonLat) {
            let tempList = [];
            let source = 'EPSG:3857';
            let destination = 'EPSG:4326';
            if (fromLonLat) {
                let temp = source;
                source = destination;
                destination = temp;
            }
            list.forEach(cs => {
                let coordinate = [];
                cs.forEach(c => {
                    let items = olProj.transform(c, source, destination);//坐标转译
                    coordinate.push(items)
                })
                tempList.push(coordinate);
            });
            return tempList;
        },
        //区域id generator
        featureIdGenerator(next_count = 1) {
            let count = this.features.length + next_count;
            if (this.idIndex !== count) {
                let index = 1;
                while (index <= count) {
                    if (this.features.some(f => f.getId() === this.idStr(index))) {
                        index++;
                    } else {
                        this.idIndex = index + 1;
                        return this.idStr(index);
                    }
                }
            }
            return this.idStr(this.idIndex++);
        },
        idStr(val) {
            return this.mapCode + '_' + val;
        },
        //显示到echarts map
        mapView() {
            if (this.tag) {
                let data = JSON.parse(this.getAllFeatureCoordinates());
                this.tag = false;
                this.$nextTick(() => this.$refs.mapView.setMapJson(data));
            } else {
                this.tag = true;
            }
        },
        //decode and edit the geo json
        editGeoJson(json) {
            let data = JSON.parse(json);
            let features = data.features;
            if (features.length && features.length > 0) {
                features.forEach(f => {
                    let coordinates = this.transformCoordinates(f.geometry.coordinates, true);
                    let feature = new Feature(new Polygon(coordinates));
                    feature.setId(f.properties.name);
                    this.drawSource.addFeature(feature);
                })
            }
        },
        saveData() {
            this.saveLoading = true;
            this.$store.dispatch('api/post', {
                url: process.env.VUE_APP_EM_ARCHIVE_URL + "/emSicCustomizedDiagramScd/saveByCode",
                data: {
                    scdCode: this.mapCode,
                    scdGeoJson: this.getAllFeatureCoordinates()
                }
            }).then(() => {
                this.$message.success("Operation success");
                this.$router.go(-1);
            }).finally(() => this.saveLoading = false);
        },
        whenKeyDown(event) {
            if (event) {
                if (event.code === 'Escape') {
                    this.clearSelected();
                } else if (event.code === 'KeyA' && (event.metaKey || event.ctrlKey)) {
                    this.selectAll();
                    event.preventDefault();
                } else if (event.code === 'KeyD' && (event.metaKey || event.ctrlKey)) {
                    this.cloneFeatures();
                    event.preventDefault();
                }
            }
        },
        clearSelected() {
            if (this.drawType !== 'None') {
                this.drawType = 'None';
            } else {
                if (this.selectedFeatures.length > 0) {
                    this.initSelection();
                }
            }
        },
        selectAll() {
            this.clearSelected();
            this.features.forEach(o => this.selectedFeatures.push(o));
        }
    }
}
</script>

<style scoped>
.w-200px {
    width: 200px;
}

.b-overlay-wrap >>> .b-overlay {
    z-index: 9000 !important;
}
</style>